<template>
  <sticky-cta-page :cta-props="ctaProps" :page-background-color="ctaProps.backgroundColor">
    <image-header-page class="image-header-page padding-bottom-xxl" :image-header-props="imageHeaderProps">
      <template v-slot:header>
        <div class="header-content">
          <div class="flex-column full-height full-width align-right header-container" >
            <div class="ri-body margin-top-sm flex-row align-center">
              <img :src="require('@/assets/icons/mortgages-flower.svg')" class="margin-bottom-xxs margin-left-xxs"/>
              המשכנתא של {{householdName}}
            </div>
            <div class="ri-medium-bold-display margin-top-l">
              נראה שכדאי למחזר את המשכנתא שלך
            </div>
            <div class="ri-large-body margin-top-m">
              המערכת האוטומטית של רייזאפ בוחנת כל יום מאות מסלולי משכנתא, ומצאה שאפשר לחסוך לך לא מעט כסף!          </div>
          </div>
          <div class="ri-body text-right">
            <div v-if="mortgageSource === 'Report' && oldMortgageBalanceDate" class="ri-body margin-top-m  margin-bottom-m black-text">
              <div class="basic-label ri-label" :style="{background: '#E8F4BA'}">
                <icon icon-name="error" size="24px" color="black" :optical-size="24" class="margin-left-s"/>
                  <div>
                    <div>מבוסס על דוח משכנתא ישן מתאריך {{ updatedAtDate }}.</div>
                    <div>כדי להעלאת דוח מעודכן
                      <span class="clickable-text" @click="onUpdateReportClick">אפשר ללחוץ כאן</span>
                    </div>
                </div>
              </div>
            </div>
            <div v-else-if="mortgageBalanceDate" class="ri-body margin-top-m  margin-bottom-m">
              {{ mortgageBalanceDateText }}
            </div>
          </div>
        </div>
      </template>
      <template v-slot:overlayContent>
        <div class="cards-container">
          <mortgage-alternative-card v-if="hasTotalDiscountTotals" class="margin-bottom-m"
                                     title="חסכון בעלות הכוללת:" :img-src="require('@/assets/mortgage-total.svg')"
                                     saving-content="אפשר לחסוך על המשכנתא שלך כ-" :saving-amount="formatAmount(totalDiscountTotals.totalDiff)"
                                     :label-text="totalDiscountLabel"
                                     @click="openTotalSavingAlternative" />
          <mortgage-alternative-card v-if="hasMonthlyDiscountTotals" title="הוזלת ההחזר החודשי:"
                                     :img-src="require('@/assets/mortgage-monthly.svg')"
                                     saving-content="אפשר לצמצם את ההחזר החודשי שלך בכ-"
                                     :saving-amount="formatAmount(monthlyDiscountTotals.monthlyDiff)"
                                     :label-text="monthlyDiscountLabel"
                                     @click="openMonthlySavingAlternative"
          />
        </div>
      </template>
    </image-header-page>
    <template v-slot:footer>
      <div class="mortgage-buttons">
        <riseup-button :title="advisoryButtonText" color="green" variant="primary"
                        @click="onContinueToAdvisoryClick"
        />
        <riseup-button :title="purchaseGuidanceButtonText" color="green" variant="secondary" class="margin-top-s"
                        @click="onPurchaseGuidanceClick"
        />
      </div>
    </template>
  </sticky-cta-page>
</template>

<script>
import BaseUI from '@riseupil/base-ui';
import Segment from '@/Segment';
import { mapActions, mapGetters, mapState } from 'vuex';
import router from '@/router';
import MortgageAlternativeCard
  from '@/pages/responsive-pages/authenticated/navigation-pages/mortgages/MortgageAlternativeCard.vue';
import Intercom from '@/Intercom';
import moneyUtils from '@/utils/money';
import MortgageMenu, { MORTGAGE_MENU_VARIANTS } from '@/pages/responsive-pages/authenticated/navigation-pages/mortgages/MortgageMenu.vue';
import moment from 'moment';
import config from '@/config';

export default {
  name: 'MortgageAlternativesPage',
  components: {
    MortgageAlternativeCard,
    ImageHeaderPage: BaseUI.Pages.ImageHeaderPage,
    StickyCtaPage: BaseUI.Pages.StickyCtaPage,
    RiseupButton: BaseUI.RiseupButton,
    Icon: BaseUI.Icon,
  },
  created() {
    Segment.trackUserInteraction('MortgagesAlternativesPage_Entered');
    Segment.trackUserInteractionToPixel('MortgagesAlternativesPage_Entered');
    this.setAppHeaderProps({
      backgroundColorVariant: BaseUI.LayoutConsts.COLOR_VARIANT.GREEN,
      textColor: BaseUI.Colors.riseupLightGreen,
      logoColor: BaseUI.Colors.riseupLightGreen,
    });
  },
  computed: {
    ...mapState('session', ['customerId']),
    ...mapGetters('session', ['householdName']),
    ...mapGetters('mortgages', ['totalDiscountTotals', 'monthlyDiscountTotals', 'currentMortgageTotals', 'createdAt',
      'reportGeneratedDate', 'mortgageSource']),
    imageHeaderProps() {
      const imgHeight = (this.mortgageSource === 'Report' && this.oldMortgageBalanceDate) ? 370 : 314;
      return {
        backgroundColor: BaseUI.LayoutConsts.COLOR_VARIANT.GREEN,
        bottomSeparator: false,
        title: 'ההתקדמות\nשלי',
        textColor: BaseUI.Colors.riseupDarkGreen,
        backgroundPosition: BaseUI.LayoutConsts.BACKGROUND_POSITIONS.BOTTOM_LEFT,
        imgHeight,
      };
    },
    ctaProps() {
      return {
        backgroundColor: BaseUI.Colors.riseupBeige,
      };
    },
    updatedAtDate() {
      if (!this.mortgageBalanceDate) {
        return '';
      }
      return moment(this.mortgageBalanceDate).format('DD.MM.YY');
    },
    updatedAtHour() {
      if (!this.mortgageBalanceDate) {
        return '';
      }
      return moment(this.mortgageBalanceDate).format('HH:mm');
    },
    mortgageBalanceDate() {
      if (this.mortgageSource === 'Report') {
        return this.reportGeneratedDate;
      }
      return this.createdAt;
    },
    mortgageBalanceDateText() {
      if (this.mortgageSource === 'Report') {
        return `מבוסס על דוח יתרות לסילוק מתאריך: ${this.updatedAtDate}`;
      }
      return `עודכן לאחרונה ב-${this.updatedAtDate} בשעה ${this.updatedAtHour}`;
    },
    oldMortgageBalanceDate() {
      if (this.mortgageSource === 'Report') {
        console.log(moment(new Date()).diff(moment(this.reportGeneratedDate), 'days'));
        return moment(new Date()).diff(moment(this.reportGeneratedDate), 'days') > 60;
      }
      return false;
    },
    totalDiscountLabel() {
      const formattedMonthlyDiff = this.formatAmount(this.totalDiscountTotals.monthlyDiff);
      return this.totalDiscountTotals.monthlyDiff > 0 ? `גם ההחזר החודשי צפוי להצטמצם ב- ${formattedMonthlyDiff} ₪ `
        : 'ההחזר החודשי צפוי להשאר פחות או יותר זהה';
    },
    monthlyDiscountLabel() {
      const formattedTotalDiff = this.formatAmount(this.monthlyDiscountTotals.totalDiff);
      return this.monthlyDiscountTotals.totalDiff > 0 ? `גם העלות הכוללת צפויה להצטמצם ב- ${formattedTotalDiff} ₪ `
        : 'העלות הכוללת צפויה להשאר פחות או יותר זהה';
    },
    hasMonthlyDiscountTotals() {
      return this.monthlyDiscountTotals?.monthlyPayment;
    },
    hasTotalDiscountTotals() {
      return this.totalDiscountTotals?.monthlyPayment;
    },
    advisoryButtonText() {
      return 'לשיחה ללא התחייבות עם הצוות';
    },
    purchaseGuidanceButtonText() {
      return 'לקניית ליווי למיחזור המשכנתא';
    },
  },
  methods: {
    ...mapActions('appHeader', ['setAppHeaderProps']),
    ...mapActions('modalRootStore', ['openModal', 'closeModal']),
    openMonthlySavingAlternative() {
      Segment.trackUserInteraction('Mortgage_OpenMonthlySavingAlternativeClicked');
      this.openMenu({
        variant: MORTGAGE_MENU_VARIANTS.MONTHLY_SAVING,
        alternativeTotals: this.monthlyDiscountTotals,
        currentMortgageTotals: this.currentMortgageTotals,
        showNextOptionButton: !!this.totalDiscountTotals,
      });
    },
    openTotalSavingAlternative() {
      Segment.trackUserInteraction('Mortgage_OpenTotalSavingAlternativeClicked');
      this.openMenu({
        variant: MORTGAGE_MENU_VARIANTS.TOTAL_SAVING,
        alternativeTotals: this.totalDiscountTotals,
        currentMortgageTotals: this.currentMortgageTotals,
        showNextOptionButton: !!this.monthlyDiscountTotals,
      });
    },
    onContinueToAdvisoryClick() {
      Segment.trackUserInteraction('Mortgage_ContinueToAdvisoryClicked');
      Segment.trackUserInteraction('buttonClicked', {
        componentName: 'MortgageAlternativesPage',
        buttonText: this.advisoryButtonText,
      });
      window.open(`${config.get().bookAppointmentUrl}?USERID=${this.customerId}`, '_blank');
    },
    onPurchaseGuidanceClick() {
      Segment.trackUserInteraction('Mortgage_PurchaseGuidanceClicked');
      Segment.trackUserInteraction('buttonClicked', {
        componentName: 'MortgageAlternativesPage',
        buttonText: this.purchaseGuidanceButtonText,
      });
      this.$router.push({ name: 'MortgageGuidanceSelectionPage' });
    },
    openMenu(menuProps) {
      this.openModal({
        component: MortgageMenu,
        props: menuProps,
        eventHandlers: {
          continue: () => {
            this.closeModal();
            Segment.trackUserInteraction('Mortgage_ContinueToAdvisoryFromMortgageDetails_Clicked');
            Segment.trackUserInteraction('buttonClicked', {
              componentName: 'MortgageAlternativesPage',
              buttonText: this.advisoryButtonText,
            });
            window.open(`${config.get().bookAppointmentUrl}?USERID=${this.customerId}`, '_blank');
          },
          next: () => {
            Segment.trackUserInteraction('Mortgage_ShowNextAlternativeClicked');
            this.closeModal();
            if (menuProps.variant === MORTGAGE_MENU_VARIANTS.TOTAL_SAVING) {
              this.openMonthlySavingAlternative();
            } else {
              this.openTotalSavingAlternative();
            }
          },
          close: () => {
            Segment.trackUserInteraction('Mortgage_ShowNextAlternativeClicked');
            this.closeModal();
          },
        },
        popupAlignment: 'menu',
      });
    },
    formatAmount(amount) {
      return moneyUtils.signedFormatAmountWithFraction(amount, 0);
    },
    openIntercomMessage() {
      Intercom.showNewMessage('מעניין אותי לשמוע עוד על מיחזור המשכנתא שלי דרך רייזאפ');
    },
    onUpdateReportClick() {
      Segment.trackUserInteraction('MortgageAlternativesPage_UpdateReportClicked');
      router.push({ path: '/upload-mortgage-report' });
    },
  },
};
</script>

<style scoped lang="scss">
@import "~@riseupil/base-ui/src/scss/riseup-colors";
@import "~@riseupil/base-ui/src/scss/spacings";

.header-container{
  text-align: right;
}

.header-content {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.text-right {
  text-align: right;
}

.image-header-page {
  background: $riseup_beige;
}

.basic-label {
  margin-top: 10px;
  width: 100%;
  padding: 10px 12px;
  border-radius: 8px;
  display: flex;
  direction: rtl;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
}

.clickable-text {
  color: $riseup_dark_green;
  text-decoration: underline;
  cursor: pointer;
  font-weight: bold;

  &:hover {
    color: darken($riseup_dark_green, 10%);
  }
}

.black-text {
  color: $riseup_black;
}

.mortgage-buttons {
  display: flex;
  flex-direction: column;
  width: 100%;

  ::v-deep .riseup-button.riseup-button {
    font-size: 16px;
  }
}
</style>
